import React, { useState } from 'react';
import { Input, Button, Typography, Card, message } from 'antd';
import { MailOutlined, LockOutlined, KeyOutlined } from '@ant-design/icons';
import { useHistory, Link } from 'react-router-dom';
import { notify } from '../../../utils/helper';
import auth from '../../../services/auth';
import { ForgetPasswordWrapper } from './Styled';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

const { Title, Text, Paragraph } = Typography;

// This would be replaced with an actual Lottie animation in production
const SecurityAnimation = () => (
  <div style={{ 
    height: '100%', 
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    position: 'relative'
  }}>
    <div style={{
      position: 'absolute',
      width: '120%',
      height: '120%',
      background: 'radial-gradient(circle at center, rgba(102, 0, 255, 0.3) 0%, rgba(51, 102, 255, 0.1) 50%, transparent 70%)',
      top: '-10%',
      left: '-10%',
      zIndex: 0
    }} />
    
    {/* Shield Animation */}
    <div style={{
      position: 'relative',
      zIndex: 2,
      textAlign: 'center'
    }}>
      <div className="pulse-container" style={{
        width: 160,
        height: 160,
        background: 'rgba(51, 102, 255, 0.1)',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 24px',
        position: 'relative',
        animation: 'pulse 2s infinite'
      }}>
        <div style={{
          width: 120,
          height: 120,
          background: 'linear-gradient(135deg, #3366ff 0%, #6600ff 100%)',
          borderRadius: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transform: 'rotate(45deg)',
          boxShadow: '0 12px 30px rgba(51, 102, 255, 0.2)',
        }}>
          <MailOutlined style={{ 
            transform: 'rotate(-45deg)', 
            color: 'white', 
            fontSize: 48
          }} />
        </div>
      </div>
      
      <Title level={2} style={{ color: 'white', marginBottom: 16 }}>Account Recovery</Title>
      <Paragraph style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: 16, maxWidth: 300 }}>
        We'll help you regain access to your Meducate account
      </Paragraph>
    </div>
    
    {/* Animated Elements */}
    <div className="floating-circle" style={{
      position: 'absolute',
      width: 200,
      height: 200,
      borderRadius: '50%',
      background: 'linear-gradient(135deg, rgba(102, 0, 255, 0.1) 0%, rgba(51, 102, 255, 0.05) 100%)',
      top: '20%',
      right: '10%',
      animation: 'float 8s ease-in-out infinite',
      zIndex: 1
    }} />
    
    <div className="floating-lock" style={{
      position: 'absolute',
      width: 60,
      height: 60,
      borderRadius: '50%',
      background: 'rgba(255, 255, 255, 0.1)',
      bottom: '30%',
      left: '15%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      animation: 'float 6s ease-in-out infinite 1s',
      zIndex: 1
    }}>
      <LockOutlined style={{ color: 'white', fontSize: 24 }} />
    </div>
    
    <div className="floating-key" style={{
      position: 'absolute',
      width: 50,
      height: 50,
      borderRadius: '50%',
      background: 'rgba(255, 255, 255, 0.1)',
      top: '30%',
      left: '25%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      animation: 'float 7s ease-in-out infinite 0.5s',
      zIndex: 1
    }}>
      <KeyOutlined style={{ color: 'white', fontSize: 20 }} />
    </div>
    
    {/* Bottom Wave */}
    <div style={{
      position: 'absolute',
      bottom: 0,
      left: 0,
      width: '100%',
      height: '20%',
      background: 'linear-gradient(to top, rgba(102, 0, 255, 0.2) 0%, transparent 100%)',
      clipPath: 'polygon(0 50%, 100% 0, 100% 100%, 0% 100%)',
      zIndex: 0
    }} />
  </div>
);

const ForgotPasswordPage = () => {
  const [loading, setLoading] = useState(false);
  const [recaptchaLoading, setRecaptchaLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const history = useHistory();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const validateEmail = () => {
    if (!email) {
      setEmailError('Please enter your email');
      return false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      setEmailError('Please enter a valid email');
      return false;
    }
    return true;
  };

  const handleRequestLink = async () => {
    if (validateEmail()) {
      setLoading(true);
      try {
        // Execute reCAPTCHA verification
        if (!executeRecaptcha) {
          notify('error', 'reCAPTCHA not available. Please refresh the page and try again.');
          setLoading(false);
          return;
        }

        setRecaptchaLoading(true);
        let recaptchaToken;
        try {
          // Execute reCAPTCHA with 'forgot_password' action
          recaptchaToken = await executeRecaptcha('forgot_password');
          // console.log('reCAPTCHA token:', recaptchaToken);
          
          if (!recaptchaToken) {
            notify('error', 'reCAPTCHA verification failed. Please try again.');
            setLoading(false);
            setRecaptchaLoading(false);
            return;
          }
        } catch (recaptchaError) {
          // console.error('reCAPTCHA error:', recaptchaError);
          notify('error', 'reCAPTCHA verification failed. Please try again.');
          setLoading(false);
          setRecaptchaLoading(false);
          return;
        } finally {
          setRecaptchaLoading(false);
        }

        const response = await auth.forgetPassword({
          email,
          reCaptchaToken: recaptchaToken // Add reCAPTCHA token to payload
        });
        if(response.status === 200 || response.status === 201){
          notify('success', 'If you are registered in our system, You shall receive email with further instructions to reset your password.');
          setLoading(false);
          // history.push('/login');
        }
      } catch(error) {
        notify('error', error.response?.data?.message || 'Failed to send password reset email. Please try again.');
        // console.log(error)
        setLoading(false);
      }
    }
  };

  return (
    // <div style={{ 
    //   height: '100vh', 
    //   overflow: 'hidden',
    //   display: 'flex',
    // }}>
    <ForgetPasswordWrapper>
      {/* Left side - Illustration (40%) */}
      <div style={{ 
        flex: '0 0 40%', 
        background: 'linear-gradient(135deg, #111122 0%, #1a1a40 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <SecurityAnimation />
      </div>
      
      {/* Right side - Form (60%) */}
      <div style={{ 
        flex: '0 0 60%', 
        background: 'white',
        overflowY: 'auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px'
      }}>
        <div style={{ maxWidth: 450, width: '100%' }}>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <Title level={2} style={{ fontSize: 32, marginBottom: 8 }}>Forgot Password</Title>
            <Text type="secondary" style={{ fontSize: 16 }}>
              Reset your Meducate account password
            </Text>
          </div>
          
          <Card
            bordered={false}
            style={{ 
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
              borderRadius: 12
            }}
            bodyStyle={{ padding: 32 }}
          >
            <Paragraph style={{ marginBottom: 24 }}>
              Enter your email address and we'll send you a password reset link.
            </Paragraph>
            
            <div style={{ marginBottom: 24 }}>
              <Text style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Email Address</Text>
              <Input 
                prefix={<MailOutlined style={{ color: 'rgba(0, 0, 0, 0.25)' }} />} 
                placeholder="Enter your email" 
                size="large" 
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailError) setEmailError('');
                }}
                status={emailError ? 'error' : ''}
                style={{ 
                  height: 50,
                  borderRadius: 8
                }}
              />
              {emailError && <Text type="danger" style={{ marginTop: 4, display: 'block' }}>{emailError}</Text>}
            </div>

            <Button 
              type="primary" 
              size="large" 
              block
              loading={loading || recaptchaLoading}
              onClick={handleRequestLink}
              style={{
                height: 50,
                backgroundImage: 'linear-gradient(135deg, #3366ff 0%, #6600ff 100%)',
                border: 'none',
                borderRadius: 8,
                fontSize: 16,
                fontWeight: 500
              }}
            >
              {recaptchaLoading ? 'Verifying...' : 'Send Reset Link'}
            </Button>

            <div style={{ textAlign: 'center', marginTop: 32 }}>
              <Text type="secondary">
                Remember your password? <Link to="/login" style={{ color: '#3366ff', fontWeight: 500 }}>Sign In</Link>
              </Text>
            </div>
          </Card>
          
          {/* reCAPTCHA Badge */}
          <div className="recaptcha-badge">
            <div className="recaptcha-badge-icon">
              ✓
            </div>
            <span>Protected by reCAPTCHA</span>
          </div>
        </div>
      </div>
      
      {/* CSS for animations */}
      <style jsx>{`
        @keyframes float {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
          100% { transform: translateY(0px); }
        }
        
        @keyframes pulse {
          0% { box-shadow: 0 0 0 0 rgba(51, 102, 255, 0.4); }
          70% { box-shadow: 0 0 0 20px rgba(51, 102, 255, 0); }
          100% { box-shadow: 0 0 0 0 rgba(51, 102, 255, 0); }
        }

        .recaptcha-badge {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          margin-top: 16px;
          padding: 8px 12px;
          background: rgba(0, 0, 0, 0.04);
          border-radius: 6px;
          font-size: 12px;
          color: rgba(0, 0, 0, 0.45);
        }

        .recaptcha-badge-icon {
          width: 16px;
          height: 16px;
          background: #52c41a;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 10px;
          font-weight: bold;
        }
      `}</style>
    </ForgetPasswordWrapper>
  );
};

export default ForgotPasswordPage;